import type { ResolvedPortfolioEnergyAsset } from "@/portfolio/energyRequests";
import type { PortfolioEnergyValue } from "@/portfolio/energyValues";
import type { Portfolio, PortfolioType } from "@/portfolio/types";
import type { Feature, FeatureCollection, Point } from "geojson";

export type PortfolioEnergyValueState = "loading" | "loaded" | "missing" | "failed" | "stale" | "invalid-configuration";

export type PortfolioEnergyFeatureProperties = {
  assetId: string;
  name: string;
  technology: "wind" | "solar";
  valueMw: number | null;
  valueState: PortfolioEnergyValueState;
};

export type PortfolioEnergyFeature = Feature<Point, PortfolioEnergyFeatureProperties>;

function valueState(
  asset: ResolvedPortfolioEnergyAsset,
  value: PortfolioEnergyValue | undefined,
): PortfolioEnergyValueState {
  if (asset.configurationIssue) {
    return "invalid-configuration";
  }
  return value?.state ?? "loading";
}

export function portfolioEnergyFeatureId(
  layerId: number,
  portfolioId: string,
  asset: Pick<ResolvedPortfolioEnergyAsset, "assetId" | "rowIndex" | "configurationIssue">,
) {
  const identity =
    asset.assetId && asset.configurationIssue !== "duplicate-asset-id" ? asset.assetId : `row-${asset.rowIndex}`;
  return `${layerId}:${portfolioId}:${identity}`;
}

export function projectPortfolioEnergyAssets(
  layerId: number,
  portfolio: Pick<Portfolio, "id" | "portfolioType">,
  assets: ResolvedPortfolioEnergyAsset[],
  values: ReadonlyMap<string, PortfolioEnergyValue> = new Map(),
): FeatureCollection<Point, PortfolioEnergyFeatureProperties> {
  const technology = portfolio.portfolioType as PortfolioType.Wind | PortfolioType.Solar;
  return {
    type: "FeatureCollection",
    features: assets.flatMap((asset): PortfolioEnergyFeature[] => {
      if (!asset.coordinates) {
        return [];
      }
      const value = asset.assetId ? values.get(asset.assetId) : undefined;
      return [
        {
          type: "Feature",
          id: portfolioEnergyFeatureId(layerId, portfolio.id, asset),
          geometry: {
            type: "Point",
            coordinates: [asset.coordinates.lon, asset.coordinates.lat],
          },
          properties: {
            assetId: asset.assetId ?? "",
            name: asset.name,
            technology,
            valueMw: value?.state === "loaded" ? value.valueMw : null,
            valueState: valueState(asset, value),
          },
        },
      ];
    }),
  };
}
